import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getToken, setToken as saveToken, clearToken } from './hiddenAuth';

// The local file-drop service (Cloudflare Tunnel -> Mac). On localhost we talk to
// the local service directly so the page is testable in dev.
const FILES_BASE =
  process.env.REACT_APP_FILES_BASE ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8600'
    : 'https://files.fabianmorelli.de');

const CHUNK = 8 * 1024 * 1024; // 8 MB — safely under Cloudflare's ~100 MB request cap

function human(b) {
  if (b == null) return '';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(i ? 1 : 0)} ${u[i]}`;
}

function when(s) {
  try { return new Date(s * 1000).toLocaleString(); } catch (e) { return ''; }
}

export default function Drop() {
  const [token, setToken] = useState(getToken);
  const [phase, setPhase] = useState(() => (getToken() ? 'unlocked' : 'locked'));
  const [passInput, setPassInput] = useState('');
  const [lockError, setLockError] = useState('');
  const [conn, setConn] = useState('unknown'); // 'online' | 'offline' | 'unknown'
  const [info, setInfo] = useState(null);

  const [path, setPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploads, setUploads] = useState([]); // {uid, name, pct, status}
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const auth = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const lock = useCallback(() => {
    clearToken();
    setToken('');
    setItems([]);
    setPath('');
    setPhase('locked');
  }, []);

  const checkHealth = useCallback(async (tok) => {
    try {
      const res = await fetch(`${FILES_BASE}/health`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.status === 401) return 'unauthorized';
      if (!res.ok) return 'offline';
      return 'online';
    } catch (e) { return 'offline'; }
  }, []);

  const refresh = useCallback(async (p = path) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${FILES_BASE}/list?path=${encodeURIComponent(p)}`, { headers: auth() });
      if (res.status === 401) { lock(); return; }
      if (!res.ok) { setConn('offline'); return; }
      const data = await res.json();
      setItems(data.items || []);
      setConn('online');
      const h = await fetch(`${FILES_BASE}/health`, { headers: auth() });
      if (h.ok) setInfo(await h.json());
    } catch (e) { setConn('offline'); }
    finally { setLoading(false); }
  }, [path, token, auth, lock]);

  useEffect(() => { if (phase === 'unlocked') refresh(path); }, [phase, path, refresh]);

  async function unlock(e) {
    e.preventDefault();
    const tok = passInput.trim();
    if (!tok) return;
    setLockError('');
    const r = await checkHealth(tok);
    if (r === 'unauthorized') { setLockError('Wrong password.'); return; }
    if (r === 'offline') { setLockError("Can't reach your Mac — is it on with the file service + tunnel running?"); return; }
    saveToken(tok);
    setToken(tok);
    setConn('online');
    setPassInput('');
    setPhase('unlocked');
  }

  // --- actions --------------------------------------------------------------

  async function download(item) {
    try {
      const res = await fetch(`${FILES_BASE}/download-ticket`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: item.path }),
      });
      if (!res.ok) { setConn('offline'); return; }
      const { ticket } = await res.json();
      const a = document.createElement('a');
      a.href = `${FILES_BASE}/download?ticket=${encodeURIComponent(ticket)}`;
      a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setConn('offline'); }
  }

  async function remove(item) {
    if (!window.confirm(`Delete ${item.name}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${FILES_BASE}/delete`, {
        method: 'DELETE', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: item.path }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        window.alert(d.detail || 'Delete failed.');
      }
    } catch (e) { /* ignore */ }
    refresh();
  }

  async function makeFolder() {
    const name = window.prompt('New folder name:');
    if (!name) return;
    try {
      await fetch(`${FILES_BASE}/mkdir`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, name }),
      });
    } catch (e) { /* ignore */ }
    refresh();
  }

  const uploadOne = useCallback(async (file) => {
    const uid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setUploads((u) => [...u, { uid, name: file.name, pct: 0, status: 'uploading' }]);
    const setU = (patch) => setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, ...patch } : x)));
    try {
      const chunks = Math.max(1, Math.ceil(file.size / CHUNK));
      const initRes = await fetch(`${FILES_BASE}/upload/init`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: file.name, size: file.size, chunks, path }),
      });
      if (!initRes.ok) throw new Error('init');
      const { id } = await initRes.json();
      for (let i = 0; i < chunks; i++) {
        const slice = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
        const r = await fetch(`${FILES_BASE}/upload/chunk/${id}/${i}`, {
          method: 'POST', headers: auth(), body: slice,
        });
        if (!r.ok) throw new Error('chunk');
        setU({ pct: Math.round(((i + 1) / chunks) * 100) });
      }
      const c = await fetch(`${FILES_BASE}/upload/complete/${id}`, { method: 'POST', headers: auth() });
      if (!c.ok) throw new Error('complete');
      setU({ pct: 100, status: 'done' });
    } catch (e) {
      setU({ status: 'error' });
    }
  }, [auth, path]);

  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    for (const f of files) await uploadOne(f);   // sequential keeps memory + order sane
    refresh();
    // clear finished rows after a moment
    setTimeout(() => setUploads((u) => u.filter((x) => x.status === 'uploading')), 4000);
  }, [uploadOne, refresh]);

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  }

  // --- navigation -----------------------------------------------------------

  const crumbs = path ? path.split('/') : [];
  const goTo = (idx) => setPath(idx < 0 ? '' : crumbs.slice(0, idx + 1).join('/'));

  // --- render ---------------------------------------------------------------

  if (phase === 'locked') {
    return (
      <div className="drop-lock">
        <form className="drop-lock-card" onSubmit={unlock}>
          <h1>File Drop</h1>
          <p className="drop-sub">Enter the password to access your Mac folder.</p>
          <input
            type="password"
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            placeholder="Password"
            autoFocus
          />
          <button type="submit">Unlock</button>
          {lockError && <p className="drop-error">{lockError}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="drop-shell">
      <div className="drop-bar">
        <span className="drop-title">File Drop</span>
        <span className={`drop-status drop-status-${conn}`}>
          <span className="drop-dot" /> {conn === 'online' ? 'connected' : conn === 'offline' ? 'Mac offline' : 'checking…'}
        </span>
        {info && <span className="drop-free">{human(info.free_bytes)} free</span>}
        <button className="drop-lockbtn" onClick={lock}>Lock</button>
      </div>

      {conn === 'offline' && (
        <div className="drop-banner">
          Can't reach the file service. Make sure your Mac is on and the file-drop service + tunnel are running.
        </div>
      )}

      <div className="drop-toolbar">
        <div className="drop-crumbs">
          <button className="drop-crumb" onClick={() => goTo(-1)}>MacDrop</button>
          {crumbs.map((c, i) => (
            <span key={i}>
              <span className="drop-crumb-sep">/</span>
              <button className="drop-crumb" onClick={() => goTo(i)}>{c}</button>
            </span>
          ))}
        </div>
        <div className="drop-actions">
          <button onClick={makeFolder}>New folder</button>
          <button className="drop-upload-btn" onClick={() => fileRef.current?.click()}>Upload files</button>
          <input
            ref={fileRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
          />
        </div>
      </div>

      <div
        className={`drop-zone${dragging ? ' over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {loading && <p className="drop-muted">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="drop-muted">This folder is empty. Drag files here or use “Upload files”.</p>
        )}
        {!loading && items.length > 0 && (
          <table className="drop-table">
            <thead>
              <tr><th>Name</th><th>Size</th><th>Modified</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.path}>
                  <td>
                    {it.is_dir ? (
                      <button className="drop-name drop-folder" onClick={() => setPath(it.path)}>
                        📁 {it.name}
                      </button>
                    ) : (
                      <span className="drop-name">📄 {it.name}</span>
                    )}
                  </td>
                  <td className="drop-num">{it.is_dir ? '—' : human(it.size)}</td>
                  <td className="drop-num">{when(it.mtime)}</td>
                  <td className="drop-row-actions">
                    {!it.is_dir && <button onClick={() => download(it)}>Download</button>}
                    <button className="drop-del" onClick={() => remove(it)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {dragging && <div className="drop-overlay">Drop to upload</div>}
      </div>

      {uploads.length > 0 && (
        <div className="drop-uploads">
          {uploads.map((u) => (
            <div key={u.uid} className={`drop-up drop-up-${u.status}`}>
              <span className="drop-up-name">{u.name}</span>
              <span className="drop-up-bar"><span className="drop-up-fill" style={{ width: `${u.pct}%` }} /></span>
              <span className="drop-up-pct">
                {u.status === 'error' ? 'failed' : u.status === 'done' ? 'done' : `${u.pct}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
